/**
 * @file ProducerCertDialog.tsx
 * @description Окно «Новый сертификат» / «Сертификат» производителя (phase08.5;
 * phase19.31 — вынесено из `ProducerDetailPage`, общие поля кабинета: тип —
 * `SelectField` вместо нативного списка, даты — `DateField`, «показывать на
 * сайте» — переключатель с подписью словами). На телефоне — во весь экран.
 *
 * phase19.16, партия «Юридические документы», шаг В (SB141;
 * uiux-legal-steps-b-c §6.3): блок «Файл» (`CertificateFileField`),
 * «Показывать на сайте» с подписью «Карточка товара и страница
 * «Сертификаты и декларации»», подсказка о сроке сразу при вводе даты.
 * Новый сертификат с файлом: «Сохранить» создаёт сертификат, затем грузит
 * файл; если файл не загрузился — окно остаётся в режиме правки:
 * «Сертификат сохранён, но файл не загрузился… [Повторить загрузку]».
 * Без права каталога — только чтение.
 */

import { useState, type FormEvent } from "react";

import { CertificateFileField } from "./CertificateFileField";
import { ProducerCertFields, type CertForm } from "./ProducerCertFields";
import { Button } from "../../ui/button";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { useNotify } from "../../ui/NotifyProvider";
import { useConfirm } from "../../ui/use-confirm";
import { useHasPermission } from "../../../auth/hooks";
import { useCertificateFile } from "../../../hooks/catalog/useCertificateFile";
import { catalogProducerCertificates, type ProducerCertificate } from "../../../lib/api-catalog";
import { ApiError } from "../../../lib/api-client";

interface Props {
  producerId: string;
  /** Сертификат на правку; `null` — новый. */
  cert: ProducerCertificate | null;
  onClose: () => void;
  /** Что-то сохранено на сервере — список перечитывается. */
  onSaved: () => void;
}

const formOf = (cert: ProducerCertificate | null): CertForm => ({
  type: cert?.type ?? "SGR",
  number: cert?.number ?? "",
  issuedBy: cert?.issuedBy ?? "",
  issuedAt: cert?.issuedAt?.substring(0, 10) ?? "",
  validUntil: cert?.validUntil?.substring(0, 10) ?? "",
  isPublic: cert?.public ?? true,
});

export function ProducerCertDialog({ producerId, cert, onClose, onSaved }: Props) {
  const notify = useNotify();
  const canEdit = useHasPermission("SHOP_MANAGE_CATALOG");
  const { confirm, request, settle } = useConfirm();
  const [saved, setSaved] = useState<ProducerCertificate | null>(cert);
  const [changed, setChanged] = useState(false);
  const [form, setForm] = useState<CertForm>(() => formOf(cert));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Сертификат создан, а файл не загрузился: файл для «Повторить загрузку». */
  const [failedFile, setFailedFile] = useState<File | null>(null);
  const file = useCertificateFile(producerId, saved, (next) => {
    setSaved(next);
    setChanged(true);
  });
  const busy = submitting || file.progress !== null;

  function close() {
    if (changed) onSaved();
    onClose();
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.number.trim()) return setError("Укажите номер документа");
    if (form.issuedAt && form.validUntil && form.validUntil < form.issuedAt) return setError("Срок действия раньше даты выдачи");
    setSubmitting(true);
    try {
      const payload = {
        type: form.type,
        number: form.number.trim(),
        issuedBy: form.issuedBy.trim() || undefined,
        issuedAt: form.issuedAt ? new Date(form.issuedAt).toISOString() : undefined,
        validUntil: form.validUntil ? new Date(form.validUntil).toISOString() : undefined,
        public: form.isPublic,
      };
      const next = saved ? await catalogProducerCertificates.update(producerId, saved.id, payload) : await catalogProducerCertificates.create(producerId, payload);
      setSaved(next);
      setChanged(true);
      // Файл к отправке: не загрузившийся в прошлый раз или выбранный для нового — не теряется.
      const toUpload = failedFile ?? (saved ? null : file.pending);
      if (toUpload && !(await file.upload(next, toUpload))) {
        // Частичный сбой: сертификат есть, файла нет — окно в режиме правки.
        setFailedFile(toUpload);
        return;
      }
      setFailedFile(null);
      notify.success("Сертификат сохранён");
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить сертификат");
    } finally {
      setSubmitting(false);
    }
  }

  async function retryUpload() {
    if (saved && failedFile && (await file.upload(saved, failedFile))) setFailedFile(null);
  }

  async function removeFile() {
    if (!saved) return file.clearPending();
    const ok = await confirm({
      title: "Убрать файл?",
      description: "Документ пропадёт со страницы «Сертификаты и декларации», на карточке товара останется номер.",
      action: "Убрать",
      danger: true,
    });
    if (ok) await file.remove(saved);
  }

  return (
    <Dialog open onOpenChange={(o) => (!o ? close() : null)}>
      {/* Как CloseSiteDialog: прокручивается окно целиком, футер прилипает к низу (QA 2026-10-20). */}
      <DialogContent fullScreen className="sm:max-h-[90vh] sm:max-w-[480px] sm:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{saved ? "Сертификат" : "Новый сертификат"}</DialogTitle>
        </DialogHeader>
        <form id="cert-form" onSubmit={(e) => void submit(e)} className="flex flex-col gap-3">
          <ProducerCertFields value={form} onChange={setForm} disabled={busy || !canEdit} />
          <CertificateFileField
            file={saved?.file ?? null}
            pending={file.pending}
            progress={file.progress}
            error={failedFile ? null : file.error}
            saved={saved !== null}
            disabled={busy}
            readOnly={!canEdit}
            onPick={(f) => {
              setFailedFile(null);
              file.pick(f);
            }}
            onOpen={() => saved && file.open(saved)}
            onRemove={() => void removeFile()}
          />
          {failedFile ? (
            <NoticeStrip
              tone="error"
              action={
                <Button type="button" variant="outline" onClick={() => void retryUpload()} disabled={busy}>
                  Повторить загрузку
                </Button>
              }
            >
              Сертификат сохранён, но файл не загрузился: {file.error ?? "ошибка загрузки"}.
            </NoticeStrip>
          ) : null}
          {error ? <NoticeStrip tone="error">{error}</NoticeStrip> : null}
        </form>
        <DialogFooter className="sticky -bottom-6 z-10 -mx-6 -mb-6 flex-row justify-end bg-surface-lowest px-6 py-3 [box-shadow:0_-1px_0_var(--outline-variant)] max-sm:mt-auto">
          <Button variant="ghost" onClick={close} disabled={busy}>
            {canEdit ? "Отмена" : "Закрыть"}
          </Button>
          {canEdit ? (
            <Button type="submit" form="cert-form" loading={busy}>
              {submitting && file.progress !== null ? `Загружаем файл — ${file.progress}%` : submitting ? "Сохраняем…" : "Сохранить"}
            </Button>
          ) : null}
        </DialogFooter>
        <ConfirmDialog request={request} onSettle={settle} />
      </DialogContent>
    </Dialog>
  );
}
